import {
  FieldValues,
  SubmitErrorHandler,
  useForm,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';

import { useCallback, useMemo } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import CustomRequiredMarkForm, {
  CustomRequiredMarkFormProps,
} from '../components/CustomRequiredMarkForm';
import InputsMapper from '../components/InputsMapper';
import { OmitControlControlledInputProps } from '../components/RHFControlledInput';
import useBuildControlledInput, {
  ControlledInputComponent,
} from './useBuildControlledInput';
import { convertNullsToUndefined } from '../zod.utils';

/* -------------------------------------------------------------------------- */
/*                           Controlled Input Types                           */
/* -------------------------------------------------------------------------- */

type Schema = z.ZodTypeAny | FieldValues;

type schemaType<S extends Schema> = S extends z.ZodTypeAny ? z.infer<S> : S;

type ListInputFormProps<S extends Schema> = UseFormProps<schemaType<S>> & {
  /**
   * The Zod schema that defines the shape, types, and validation rules for the form.
   *
   * This schema is the single source of truth for the form's validation logic. It is
   * passed directly to `@hookform/resolvers/zod` to integrate seamlessly with
   * React Hook Form, providing real-time, type-safe validation.
   *
   * The type of the form's data (`schemaType<S>`) is automatically inferred
   * from this schema, ensuring type safety throughout the component.
   *
   * @example
   * import { z } from 'zod';
   *
   * const loginSchema = z.object({
   *   email: z.string().email('Please enter a valid email address.'),
   *   password: z.string().min(8, 'Password must be at least 8 characters long.'),
   *   rememberMe: z.boolean().optional(),
   * });
   *
   * // You would then pass `loginSchema` to this prop.
   *
   * @see https://zod.dev/ - For more information on creating Zod schemas.
   * @see https://github.com/react-hook-form/resolvers#zod - For its usage with React Hook Form.
   */
  schema?: S;

  /**
   * Defines the form fields to be rendered. This prop is flexible and accepts
   * two different structures to accommodate simple and complex layouts:
   *
   * 1.  **A flat array (`OmitControlControlledInputProps[]`)**:
   *     Use this for a standard form where all inputs are rendered sequentially
   *     in a single column or list.
   *
   *     @example
   *     // Renders 'firstName' then 'email' in a single group.
   *     inputs={[
   *       { name: 'firstName', label: 'First Name' },
   *       { name: 'email', label: 'Email', inputType: 'email' }
   *     ]}
   *
   * 2.  **A nested array (`OmitControlControlledInputProps[][]`)**:
   *     Use this to group inputs, often for creating row-based layouts where
   *     multiple fields appear on the same line. Each inner array represents one
   *     group or row, which will be passed to its own `InputsMapper`.
   *
   *     @example
   *     // Renders 'firstName' and 'lastName' in the first row,
   *     // and 'email' in a second row.
   *     inputs={[
   *       [ { name: 'firstName', label: 'First Name' }, { name: 'lastName', label: 'Last Name' } ],
   *       [ { name: 'email', label: 'Email', inputType: 'email' } ]
   *     ]}
   *
   * The prop is optional. If not provided, no inputs will be rendered.
   */
  inputs?:
    | OmitControlControlledInputProps<schemaType<S>>[]
    | OmitControlControlledInputProps<schemaType<S>>[][];
};

type CustomFormProps<S extends Schema> = Omit<
  CustomRequiredMarkFormProps<schemaType<S>>,
  'methods'
> & {
  onSubmit?: (data: schemaType<S>) => void;
  onError?: SubmitErrorHandler<schemaType<S>>;
  shouldConvertNullsToUndefined?: boolean;
};

export type RHFFormReturnType<S extends Schema> = {
  methods: UseFormReturn<Partial<schemaType<S>>>;
  ControlledInput: ControlledInputComponent<S>;
  Form: React.FC<CustomFormProps<S>>;
  renderedFormInputs: React.ReactNode;
};

const useRHFForm = <S extends Schema>(
  props: ListInputFormProps<S>,
): RHFFormReturnType<S> => {
  const {
    schema,
    inputs,
    mode = 'all',
    shouldFocusError = true,
    ...rest
  } = props;

  const formOptions: { resolver?: any } = {};

  if (schema instanceof z.ZodType) {
    formOptions.resolver = zodResolver(schema);
  }

  const methods = useForm<schemaType<S>>({
    mode,
    shouldFocusError,
    ...formOptions,
    ...rest,
  });
  const { control } = methods;

  //

  //

  //

  /* -------------------------------------------------------------------------- */
  /*                              Controlled Input                              */
  /* -------------------------------------------------------------------------- */

  //#region Controlled Input

  const ControlledInput = useBuildControlledInput({ control });

  const CustomForm = useCallback(
    (formProps: CustomFormProps<S>) => {
      const {
        onSubmit = () => {},
        onError = () => {},
        onFinish,
        shouldConvertNullsToUndefined,
        ...rest
      } = formProps;

      let customProps: CustomFormProps<S> = {};

      if (schema instanceof z.ZodType) {
        customProps = { schema };
      }

      const transformSubmitData = (data: schemaType<S>) => {
        if (shouldConvertNullsToUndefined) {
          return onSubmit(convertNullsToUndefined(data));
        }

        return onSubmit(data);
      };

      return (
        <CustomRequiredMarkForm
          {...rest}
          methods={methods}
          {...customProps}
          onFinish={
            onFinish
              ? onFinish
              : methods.handleSubmit(transformSubmitData, onError)
          }
        />
      );
    },
    [methods, schema],
  );

  const renderedFormInputs = useMemo(() => {
    if (!inputs) return [];
    if (!Array.isArray(inputs)) return [];

    if (Array.isArray(inputs[0])) {
      return inputs?.map((inputList, index) => {
        return (
          <InputsMapper
            key={`input-mapper-${index}`}
            fields={
              inputList as OmitControlControlledInputProps<schemaType<S>>[]
            }
          />
        );
      });
    }

    return [
      <InputsMapper
        key="input-mapper"
        fields={inputs as OmitControlControlledInputProps<schemaType<S>>[]}
      />,
    ];
  }, [inputs]);

  return {
    ControlledInput,
    Form: CustomForm,
    renderedFormInputs,
    methods: methods as unknown as UseFormReturn<Partial<schemaType<S>>>,
  };
};

export default useRHFForm;
